/**
 * ★ docKey 단일 정본 레지스트리 (STEP 0)
 *
 * 한 문서(docKey)에 대해 흩어져 있던 네 곳을 여기 하나로 합쳤다.
 *   ① 사이드바 메뉴 레지스트리 — automationMenuConfig.ts 의 DOCUMENT_MENU_ITEMS
 *   ② 라우트 테이블          — App.tsx 의 /crm/document-automation/* · /automation/*
 *   ③ 실행 로직 경로 맵      — pipelinePrefill.ts 의 ALL_AUTOMATION_DOCS,
 *                              documentAutomationOutput.ts 의 DOC_KEY_TO_HISTORY_TYPE ·
 *                              DOC_TYPE_LABELS · DOC_TYPE_TO_PAGE_ROUTE
 *   ④ 구현 메타              — documentAutomationConfig.ts 의 pageFile/previewComponent/
 *                              printRootClass/driveCaptureTarget/riskLevel
 * 위 네 모듈은 이제 전부 이 파일을 파생해서 같은 이름으로 다시 내보낸다.
 * 새 문서를 붙이거나 경로·라벨을 고칠 때는 여기만 고친다.
 *
 * ★ 화면은 바뀌지 않는다 — 파생 결과(배열 순서·라벨·status·경로)는 합치기 전과 값이 같다.
 *   순서가 화면에 보이는 소비처가 있다(StageMoveModal 이 ALL_AUTOMATION_DOCS 를 그대로 그린다).
 */

// ─── docKey 정본 ────────────────────────────────────────────────────────────

/**
 * 문서 키. 소문자-하이픈이 정본이다(사이드바·플로우빌더·라우트가 이 표기를 쓴다).
 * PascalCase("Quotation" 등)는 하위호환 별칭일 뿐 정본이 아니다 → LEGACY_EXECUTION_ALIASES.
 */
export type DocKey =
  | "quote"
  | "quote-other"
  | "quote-equipment"
  | "contracts"
  | "contract"
  | "contract-integration"
  | "invoice"
  | "mou"
  | "maintenance-mou"
  | "mou-dematic"
  | "nda"
  | "nda-international"
  | "order-approval"
  | "proposal";

export type DocumentStatus = "active" | "beta" | "development" | "coming_soon";

/**
 * 문서 전환 레일(목업 ①)이 쓰는 묶음. 목업이 칩 3개로 그리는 그 묶음이다.
 * 사이드바 메뉴 그룹(Quotation/MOU)과는 다른 축이라 따로 둔다.
 */
export type DocumentFamily = "quote-billing" | "contract-agreement" | "internal-approval";

export const DOCUMENT_FAMILY_LABELS: Readonly<Record<DocumentFamily, string>> = {
  "quote-billing": "견적 · 청구",
  "contract-agreement": "계약 · 협약",
  "internal-approval": "내부 결재",
};

/** 레일 묶음 영문 표시 (화면 전용 — 값은 위 ko 맵이 정본) */
export const DOCUMENT_FAMILY_LABELS_EN: Readonly<Record<DocumentFamily, string>> = {
  "quote-billing": "Quotes & billing",
  "contract-agreement": "Contracts & agreements",
  "internal-approval": "Internal approval",
};

/** 편집 섹션 라벨(폼 카드 제목)의 영문 표시 — ko 라벨을 키로 찾는다. 없으면 ko 그대로. */
export const DOCUMENT_SECTION_LABELS_EN: Readonly<Record<string, string>> = {
  "MOU 각서 정보": "MOU details",
  "견적 구성": "Quote items",
  "견적 조건": "Quote terms",
  "견적서 정보": "Quote details",
  "경비 입력": "Expenses",
  "계산 결과": "Calculation result",
  "계약 금액 구성": "Contract amount breakdown",
  "계약 내용": "Contract terms",
  "기본 선택": "Basic options",
  "기본 정보": "Basic info",
  "대금 지급 조건": "Payment terms",
  "문서 유형": "Document type",
  "상대방 정보": "Counterparty info",
  "상세 명세": "Detailed specification",
  "상세 명세 표 컬럼": "Specification table columns",
  "서명 정보": "Signature info",
  "계약 조건": "Agreement terms",
  "세부내역서": "Itemized statement",
  "외부 견적": "External quote",
  "이익률 / 지급 조건": "Margin / payment terms",
  "인건비 입력": "Labor costs",
  "인보이스 정보": "Invoice details",
  "인보이스 테이블 편집": "Invoice table",
  "재료비 입력": "Material costs",
  "조정 항목": "Adjustments",
  "첨부 이미지": "Attached images",
  "파트너 견적 업로드": "Upload partner quote",
  "프로젝트 기본정보": "Project basics",
  "하단 견적 항목서": "Quote item summary",
  "환율": "Exchange rate",
};

/** 레지스트리 문서 label(국문) → 영문 UI 표시명. 국문 label 과 같은 결("견적서" ↔ "Quote")로 맞춘다. */
export const DOCUMENT_LABELS_EN: Readonly<Record<string, string>> = {
  "견적서": "Quote",
  "견적서 (Automation)": "Quote (Automation)",
  "견적서 (Equipment)": "Quote (Equipment)",
  "견적서 (WMS)": "Quote (WMS)",
  "견적서 (시스템)": "Quote (System)",
  "시스템": "System",
  "계약서 허브": "Contract hub",
  "계약서": "Contract",
  "시스템 연동 계약서": "System integration contract",
  "계약서 (시스템 연동)": "Contract (system integration)",
  "인보이스": "Invoice",
  "유지보수 MOU": "Maintenance MOU",
  "수주 품의서": "Order approval",
  "수주품의서": "Order approval",
  "제안서": "Proposal",
  "고객 자료 분석": "Customer data analysis",
  "3D 시뮬레이션": "3D simulation",
  "인보이스 가이드": "Invoice guide",
  "해외 NDA": "International NDA",
  "창고 3D 랙 레이아웃": "Warehouse 3D rack layout",
  "디지털 트윈 레이아웃": "Digital twin layout",
};

export function documentLabel(label: string, language: "ko" | "en" = "ko"): string {
  return language === "en" ? DOCUMENT_LABELS_EN[label] ?? label : label;
}

export function documentSectionLabel(label: string, language: "ko" | "en" = "ko"): string {
  return language === "en" ? DOCUMENT_SECTION_LABELS_EN[label] ?? label : label;
}

export function documentFamilyLabel(family: string, fallback: string, language: "ko" | "en" = "ko"): string {
  if (language !== "en") return fallback;
  // "execution"(분석 · 제안 · 시뮬레이션)은 문서 카탈로그(src/data/documentCatalog.ts)가 붙이는 묶음이다.
  if (family === "execution") return "Analysis · Proposal · Simulation";
  return DOCUMENT_FAMILY_LABELS_EN[family as DocumentFamily] ?? fallback;
}

/** 레일 칩 순서 */
export const DOCUMENT_FAMILY_ORDER: readonly DocumentFamily[] = [
  "quote-billing",
  "contract-agreement",
  "internal-approval",
];

export type DocumentRiskLevel = "low" | "medium" | "medium-high" | "high" | "very-high";

/**
 * 편집 섹션. id 는 편집 영역의 섹션과 미리보기의 대응 블록을 잇는 정본 키다
 * (STEP 2~3 에서 양쪽에 같은 id 를 심어 스크롤·하이라이트를 연결한다).
 * label 은 현재 각 페이지가 실제로 그리는 폼 카드 제목에서 가져왔다.
 *
 * ★ required 는 새로 만든 규칙이 아니라 **기존 검증에서 가져온 값**이다.
 *   이 저장소에 있는 유일한 문서 필수값 검증은 DocumentPreviewShell 의 검토 버튼
 *   (runReview → validateEditableElement) 하나뿐이다. 미리보기의 편집 가능 요소가
 *   비어 있거나 PLACEHOLDER_PATTERNS("파트너사"·"주소 입력"·"고객사명"·"Client" …)에
 *   걸리면 오류로 표시한다.
 *   → required = true 는 "새 문서 기본 상태에서 그 섹션의 값이 이 검토에 실제로 걸린다"는 뜻이다.
 *   대조 근거는 docs/SELF_REVIEW_2026-09-21_DOC_REGISTRY.md 의 required 대조표에 있다.
 */
export type DocumentSection = {
  id: string;
  label: string;
  required: boolean;
  /**
   * 이 섹션이 만드는 미리보기 편집 요소의 id (EditableRichText 의 data-editable-id).
   * 채울 항목 ↔ 편집 섹션 ↔ 미리보기 블록 3방향 연동과 완료/미완 판정이 이 목록을 쓴다.
   * DOM 에 없는 id 는 무시된다(문서·언어에 따라 안 그려지는 블록이 있다).
   * 비어 있으면 그 섹션은 "상태 없음"(회색) — 대응하는 미리보기 편집 요소가 없다는 뜻이다.
   */
  previewEditableIds?: readonly string[];
};

export type DocumentMenuPlacement = {
  /** 사이드바 표기 이름 (문서 정본 이름과 다를 수 있다) */
  label: string;
  status: DocumentStatus;
  /** DOCUMENT_MENU_ITEMS.path */
  path: string;
  /** `/crm/document-automation/${key}` 규칙 밖일 때만 — 사이드바가 이 경로를 그대로 쓴다 */
  absolutePath?: string;
  /**
   * 권한 관리 화면(/settings/menus)이 쓰는 표시 이름. 사이드바 라벨과 다르다
   * ("WMS" → "견적서 > WMS"). 없으면 사이드바 라벨을 그대로 쓴다.
   */
  roleManifestLabel?: string;
};

/** 구현 위치 메타 — 코드를 찾아갈 때 쓰는 지도. 런타임 로직이 읽지는 않는다. */
export type DocumentImplMeta = {
  pageFile: string;
  previewComponent: string;
  /** DocumentPrintRoot 에 넘기는 추가 클래스 (공통 클래스 .document-print-root 는 항상 붙는다) */
  printRootClass: string;
  /** 미리보기 루트의 data-doc-surface 값 */
  docSurface?: string;
  /** Drive 업로드·PDF 캡처가 잡는 DOM */
  driveCaptureTarget: string;
  editable: boolean;
  printCssLocation: readonly string[];
  /** 미평가 문서는 비워 둔다 — 임의로 채우지 않는다 */
  riskLevel?: DocumentRiskLevel;
  notes?: string;
};

export type DocumentRegistryEntry = {
  key: DocKey;
  /** "hub" 는 문서가 아니라 목록 화면(계약서 허브) — 메뉴에만 있고 문서 실행 경로는 없다 */
  kind: "document" | "hub";
  /** 문서 정본 이름 */
  label: string;
  /** 문서 전환 레일의 묶음. 없으면 레일에 올리지 않는다(제안서처럼 폼이 다른 화면). */
  family?: DocumentFamily;
  /**
   * 통합 작성 화면(/crm/documents/<key>)에 올라간 문서.
   * 임베드 캡처도 이 경로를 쓴다 — documentPageComponents 의 키와 1:1 이어야 한다(테스트가 확인).
   */
  workspace?: true;
  /** documentAutomationConfig 의 camelCase 구 키 (있는 문서만) */
  legacyConfigKey?: string;
  routes: {
    /** /crm 영역 경로 */
    crm: string;
    /** /automation 영역 경로 (없는 문서도 있다) */
    automation?: string;
  };
  menu?: DocumentMenuPlacement;
  /** 플로우빌더·단계이동 모달이 쓰는 실행 진입점 (ALL_AUTOMATION_DOCS) */
  execution?: {
    path: string;
    /** ★ 메뉴 라벨과 다르다 — 실행 목록은 "견적서 (WMS)" 처럼 문서 종류를 먼저 쓴다 */
    label: string;
  };
  /** crm_document_authoring_histories.document_type */
  historyType?: string;
  /** 이력 화면 표시 라벨 (DOC_TYPE_LABELS) */
  historyLabel?: string;
  /** DOC_KEY_TO_HISTORY_TYPE 이 같은 문서로 받아 주는 별칭 docKey */
  historyAliasKeys?: readonly string[];
  /**
   * docKey → document_type 역방향 조회에서 뺀다.
   * 플로우빌더 산출물 docKey 로는 잡히지 않던 문서 — 합치기 전 동작을 그대로 유지하려고 남긴 표시.
   */
  excludeFromDocKeyHistory?: true;
  impl?: DocumentImplMeta;
  display?: {
    tabLabel?: string;
    subheaderTitle?: string;
    badgeLabel?: string;
    driveFolderLabel?: string;
  };
  /** 같은 문서를 부르는 다른 업무 용어 */
  aliases?: readonly string[];
  sections: readonly DocumentSection[];
};

// ─── 레지스트리 본문 ─────────────────────────────────────────────────────────

export const DOCUMENT_REGISTRY: readonly DocumentRegistryEntry[] = [
  {
    key: "quote",
    kind: "document",
    workspace: true,
    label: "견적서",
    family: "quote-billing",
    legacyConfigKey: "quote",
    routes: { crm: "/crm/document-automation/quote", automation: "/automation/quote" },
    menu: { label: "시스템", status: "active", path: "/crm/document-automation/quote", roleManifestLabel: "견적서 > 시스템" },
    execution: { path: "/crm/document-automation/quote", label: "견적서 (시스템)" },
    historyType: "quote",
    historyLabel: "견적서 (시스템)",
    historyAliasKeys: ["quotation"],
    impl: {
      pageFile: "QuotePage.tsx",
      previewComponent: "QuotePreview",
      printRootClass: "quote-print-root",
      docSurface: "quote",
      driveCaptureTarget: ".doc-preview-track",
      editable: true,
      printCssLocation: ["QuotePage.tsx", "DocStyles.css"],
      riskLevel: "high",
    },
    display: { tabLabel: "견적서", badgeLabel: "견적서", driveFolderLabel: "견적서" },
    sections: [
      // quote-client-name 이 기본 상태에서 빈 값 → 기존 검토가 "내용이 비어 있습니다"로 잡는다.
      { id: "customer", label: "상대방 정보", required: true, previewEditableIds: ["quote-client-name"] },
      { id: "quote-info", label: "견적서 정보", required: false, previewEditableIds: ["quote-title", "quote-send-date", "quote-validity"] },
      { id: "basics", label: "기본 선택", required: false },
      { id: "items", label: "견적 구성", required: false },
      { id: "scope", label: "공급 범위", required: false },
    ],
  },
  {
    key: "quote-other",
    kind: "document",
    workspace: true,
    label: "견적서 (Automation)",
    family: "quote-billing",
    routes: { crm: "/crm/document-automation/quote-other", automation: "/automation/quote-other" },
    menu: { label: "Automation", status: "active", path: "/crm/document-automation/quote-other", roleManifestLabel: "견적서 > Automation" },
    execution: { path: "/crm/document-automation/quote-other", label: "견적서 (Automation)" },
    historyType: "quote_other",
    historyLabel: "견적서 (Automation)",
    impl: {
      pageFile: "OtherQuotePage.tsx",
      previewComponent: "OtherQuotePreview",
      printRootClass: "other-quote-print-root",
      docSurface: "other-quote",
      driveCaptureTarget: ".doc-preview-track",
      editable: true,
      printCssLocation: ["OtherQuotePage.tsx", "DocStyles.css"],
      notes: "리스크 미평가 — 구 메타 모듈에 항목이 없던 문서",
    },
    display: { tabLabel: "Automation", driveFolderLabel: "견적서" },
    sections: [
      { id: "basics", label: "기본 정보", required: false },
      { id: "partner-upload", label: "파트너 견적 업로드", required: false },
      { id: "fx", label: "환율", required: false },
      { id: "details", label: "상세 명세", required: false },
      { id: "adjustments", label: "조정 항목", required: false },
      { id: "terms", label: "견적 조건", required: false, previewEditableIds: ["oq-scope-notes"] },
      { id: "summary-items", label: "하단 견적 항목서", required: false },
      { id: "scope", label: "공급 범위", required: false },
      { id: "detail-columns", label: "상세 명세 표 컬럼", required: false },
      { id: "attachments", label: "첨부 이미지", required: false },
    ],
  },
  {
    key: "quote-equipment",
    kind: "document",
    label: "견적서 (Equipment)",
    family: "quote-billing",
    routes: { crm: "/crm/document-automation/quote-equipment" },
    menu: { label: "Equipment", status: "development", path: "/crm/document-automation/quote-equipment", roleManifestLabel: "견적서 > Equipment" },
    execution: { path: "/crm/document-automation/quote-equipment", label: "견적서 (Equipment)" },
    display: { tabLabel: "Equipment" },
    // 화면·라우트가 아직 없다(개발중) — 섹션도 정의하지 않는다.
    sections: [],
  },
  {
    key: "contracts",
    kind: "hub",
    label: "계약서 허브",
    routes: { crm: "/crm/contracts" },
    menu: {
      label: "Contract(계약서)",
      status: "active",
      path: "/crm/contracts",
      absolutePath: "/crm/contracts",
      roleManifestLabel: "계약서",
    },
    // ★ 계약서 종류마다 메뉴가 늘어나던 구조를 한 화면 + 유형 선택으로 바꿨다.
    //   WMS Contract · System Integration 은 메뉴에서 내리고 유형(wms · si)으로 편입했다.
    //   화면과 라우트는 그대로 살아 있고, /crm/contracts 의 "새 계약서"에서 유형을 고르면
    //   legacy_route 로 보낸다(crm_contract_types.renderer='legacy_component').
    sections: [],
  },
  {
    key: "contract",
    kind: "document",
    workspace: true,
    label: "계약서",
    family: "contract-agreement",
    legacyConfigKey: "contract",
    routes: { crm: "/crm/document-automation/contract", automation: "/automation/contract" },
    execution: { path: "/crm/document-automation/contract", label: "계약서" },
    historyType: "contract",
    historyLabel: "계약서",
    impl: {
      pageFile: "ContractPage.tsx",
      previewComponent: "ContractPreview",
      printRootClass: "contract-print-root",
      docSurface: "contract",
      driveCaptureTarget: ".doc-preview-track",
      editable: true,
      printCssLocation: ["ContractPage.tsx", "DocStyles.css"],
      riskLevel: "very-high",
      notes: "다중 페이지/측정 DOM 영향으로 리스크가 높음",
    },
    display: { tabLabel: "계약서", badgeLabel: "계약서", driveFolderLabel: "계약서" },
    sections: [
      // ★ contract-intro 는 고객사명을 감싼 **계약 본문**이라, 고객사가 비어도 나머지 문장이 남아
      //   기존 검토가 "채움" 으로 봤다 — 아무것도 안 넣었는데 고객 정보가 파란 체크였다(사용자 지적).
      //   고객 데이터만 들어가는 서명란(발주사)으로 바꾼다. 본문은 연동용으로 함께 남긴다.
      { id: "customer", label: "상대방 정보", required: true, previewEditableIds: ["contract-client-company", "contract-client-addr", "contract-client-ceo", "contract-intro"] },
      { id: "basics", label: "기본 선택", required: false, previewEditableIds: ["contract-title", "contract-subtitle", "contract-date"] },
      { id: "terms", label: "계약 내용", required: false, previewEditableIds: ["contract-service-name", "contract-service-detail", "contract-service-period"] },
      { id: "amounts", label: "계약 금액 구성", required: false, previewEditableIds: ["contract-service-amount", "contract-payment-label", "contract-payment-note"] },
    ],
  },
  {
    key: "contract-integration",
    kind: "document",
    workspace: true,
    label: "시스템 연동 계약서",
    family: "contract-agreement",
    legacyConfigKey: "contractIntegration",
    routes: { crm: "/crm/document-automation/contract-integration" },
    historyType: "contract_integration",
    historyLabel: "계약서 (시스템 연동)",
    impl: {
      pageFile: "IntegrationContractPage.tsx",
      previewComponent: "IntegrationContractPreview",
      printRootClass: "contract-integration-print-root",
      docSurface: "contract-integration",
      driveCaptureTarget: ".doc-preview-track",
      editable: true,
      printCssLocation: ["IntegrationContractPage.tsx", "DocStyles.css"],
      riskLevel: "high",
      notes: "WAC Logistics API 연동 계약서 기반 — 표지/요약/서명/세부내역서/일반조건 다중 페이지",
    },
    display: {
      tabLabel: "시스템 연동",
      subheaderTitle: "시스템 연동 계약서 작성기",
      badgeLabel: "연동 계약서",
      driveFolderLabel: "계약서",
    },
    aliases: ["연동 계약서", "API 연동 계약서", "인터페이스 구축 계약서", "SI 계약서"],
    sections: [
      // 서명란 회사/서명자가 기본 상태에서 비어 기존 검토에 걸린다.
      // ★ signature-right 는 **공급사(콜로세움)** 란이다. 우리 회사 정보라 항상 채워져 있어,
      //   발주사를 하나도 안 넣었는데 "발주사 정보 — 채움" 이 떴다(사용자 지적). left 가 발주사다.
      { id: "client", label: "상대방 정보", required: true, previewEditableIds: ["ci-cover-client", "ci-signature-left-company", "ci-signature-left-title", "ci-signature-left-name", "ci-signature-left-date"] },
      { id: "basics", label: "기본 정보", required: false, previewEditableIds: ["ci-doc-title", "ci-cover-title", "ci-cover-date", "ci-sum-name", "ci-sum-period", "ci-sum-type"] },
      { id: "payment", label: "대금 지급 조건", required: false, previewEditableIds: ["ci-sum-amount", "ci-sum-method", "ci-sum-terms"] },
      { id: "details", label: "세부내역서", required: false, previewEditableIds: ["ci-sum-scope", "ci-detail-title"] },
      // 견적서와 같은 편집기를 쓰게 되면서 레일에도 같이 올린다(예전엔 카드에 id 가 없어 안 보였다).
      { id: "ci-fx", label: "환율", required: false },
      { id: "ci-details", label: "상세 명세", required: false },
    ],
  },
  {
    key: "invoice",
    kind: "document",
    workspace: true,
    label: "인보이스",
    family: "quote-billing",
    legacyConfigKey: "invoice",
    routes: { crm: "/crm/document-automation/invoice", automation: "/automation/invoice" },
    menu: { label: "Invoice(인보이스)", status: "active", path: "/crm/document-automation/invoice", roleManifestLabel: "Invoice(인보이스)" },
    execution: { path: "/crm/document-automation/invoice", label: "인보이스" },
    historyType: "invoice",
    historyLabel: "인보이스",
    impl: {
      pageFile: "InvoicePage.tsx",
      previewComponent: "InvoicePreview",
      printRootClass: "invoice-print-root",
      docSurface: "invoice",
      driveCaptureTarget: ".doc-preview-track",
      editable: true,
      printCssLocation: ["InvoicePage.tsx", "DocStyles.css"],
      riskLevel: "high",
    },
    display: { tabLabel: "인보이스", badgeLabel: "인보이스", driveFolderLabel: "인보이스" },
    sections: [
      // invoice-recipient 이 기본 상태에서 "{고객사명}" placeholder 로 떨어져 기존 검토에 걸린다.
      { id: "customer", label: "상대방 정보", required: true, previewEditableIds: ["invoice-recipient"] },
      { id: "invoice-info", label: "인보이스 정보", required: false, previewEditableIds: ["invoice-title", "invoice-date", "invoice-billing-month"] },
      { id: "items", label: "인보이스 테이블 편집", required: false, previewEditableIds: ["invoice-total-note", "invoice-remark-title"] },
    ],
  },
  {
    key: "mou",
    kind: "document",
    workspace: true,
    label: "MOU",
    family: "contract-agreement",
    legacyConfigKey: "mou",
    routes: { crm: "/crm/document-automation/mou", automation: "/automation/mou" },
    menu: { label: "Partnership", status: "active", path: "/crm/document-automation/mou", roleManifestLabel: "MOU > Partnership" },
    execution: { path: "/crm/document-automation/mou", label: "MOU" },
    historyType: "mou",
    historyLabel: "MOU",
    impl: {
      pageFile: "MOUPage.tsx",
      previewComponent: "MOUPreview",
      printRootClass: "standard-mou-print-root",
      docSurface: "mou",
      driveCaptureTarget: ".doc-preview-track",
      editable: true,
      printCssLocation: ["MOUPage.tsx", "DocStyles.css"],
      riskLevel: "high",
    },
    display: {
      tabLabel: "MOU",
      subheaderTitle: "MOU 작성기",
      badgeLabel: "MOU",
      driveFolderLabel: "MOU",
    },
    aliases: ["MOU", "표준 MOU"],
    sections: [
      // 파트너사명이 비면 "주식회사 파트너" placeholder 로 떨어져 기존 검토에 걸린다.
      { id: "partner", label: "상대방 정보", required: true, previewEditableIds: ["mou-partner-name", "mou-partner-name-en", "mou-partner-short", "mou-partner-address", "mou-partner-address-en", "mou-partner-intro"] },
      { id: "contract-date", label: "MOU 각서 정보", required: false, previewEditableIds: ["mou-contract-date"] },
      // 서명일(supplierDate · partner.date)이 기본 상태에서 비어 기존 검토에 걸린다.
      { id: "signature", label: "서명 정보", required: true, previewEditableIds: ["mou-signature-left-title", "mou-signature-left-name", "mou-signature-left-date", "mou-signature-right-company", "mou-signature-right-companyEn", "mou-signature-right-title", "mou-signature-right-name", "mou-signature-right-date"] },
    ],
  },
  {
    key: "maintenance-mou",
    kind: "document",
    workspace: true,
    label: "유지보수 MOU",
    family: "contract-agreement",
    legacyConfigKey: "maintenanceMou",
    routes: {
      crm: "/crm/document-automation/maintenance-mou",
      automation: "/automation/maintenance-mou",
    },
    menu: { label: "Service", status: "active", path: "/crm/document-automation/maintenance-mou", roleManifestLabel: "MOU > Service" },
    execution: { path: "/crm/document-automation/maintenance-mou", label: "유지보수 MOU" },
    historyType: "maintenance_mou",
    historyLabel: "유지보수 MOU",
    impl: {
      pageFile: "MOUPage.tsx",
      previewComponent: "MaintenanceMOUPreview",
      printRootClass: "maintenance-mou-print-root",
      docSurface: "mou-maintenance",
      driveCaptureTarget: ".doc-preview-track",
      editable: true,
      printCssLocation: ["MOUPage.tsx", "DocStyles.css"],
      riskLevel: "high",
      notes: "MOUPage 의 variant='maintenance' 분기에 해당.",
    },
    display: {
      tabLabel: "유지보수",
      subheaderTitle: "MOU 작성기",
      badgeLabel: "유지보수",
      driveFolderLabel: "유지보수 MOU",
    },
    aliases: ["유지보수", "유지보수 MOU", "Maintenance MOU"],
    sections: [
      // 파트너사명이 비면 "주식회사 파트너" placeholder 로 떨어져 기존 검토에 걸린다.
      { id: "partner", label: "상대방 정보", required: true, previewEditableIds: ["mmou-partner-name", "mmou-partner-name-en", "mmou-partner-short", "mmou-partner-address", "mmou-partner-address-en", "mmou-partner-intro"] },
      // ★ mmou-contract-date 는 이 미리보기에 **존재하지 않는 id** 였다 — 그래서 항상 회색이었다.
      //   유지보수 MOU 미리보기에는 날짜 블록이 없다(서명란 날짜뿐). 문서 제목을 대표로 쓴다.
      { id: "contract-date", label: "MOU 각서 정보", required: false, previewEditableIds: ["mmou-title"] },
      // 서명일(supplierDate · partner.date)이 기본 상태에서 비어 기존 검토에 걸린다.
      { id: "signature", label: "서명 정보", required: true, previewEditableIds: ["mmou-signature-left-title", "mmou-signature-left-name", "mmou-signature-left-date", "mmou-signature-right-company", "mmou-signature-right-companyEn", "mmou-signature-right-title", "mmou-signature-right-name", "mmou-signature-right-date"] },
    ],
  },
  {
    key: "mou-dematic",
    kind: "document",
    workspace: true,
    label: "Dematic MOU",
    family: "contract-agreement",
    routes: { crm: "/crm/document-automation/mou-dematic", automation: "/automation/mou-dematic" },
    menu: { label: "Dematic MOU", status: "active", path: "/crm/document-automation/mou-dematic", roleManifestLabel: "MOU > Dematic" },
    execution: { path: "/crm/document-automation/mou-dematic", label: "Dematic MOU" },
    historyType: "mou_dematic",
    historyLabel: "Dematic MOU",
    historyAliasKeys: ["dematic-mou"],
    impl: {
      pageFile: "MOUPage.tsx",
      previewComponent: "MOUDematicPreview",
      printRootClass: "mou-dematic-print-root",
      docSurface: "dematic-mou",
      driveCaptureTarget: ".doc-preview-track",
      editable: true,
      printCssLocation: ["MOUPage.tsx", "DocStyles.css"],
      notes: "MOUPage 의 variant='dematic' 분기(영문 전용). 리스크 미평가.",
    },
    display: { tabLabel: "Dematic", subheaderTitle: "Dematic MOU 작성기", driveFolderLabel: "MOU" },
    sections: [
      // 파트너사명이 비면 "주식회사 파트너" placeholder 로 떨어져 기존 검토에 걸린다.
      { id: "partner", label: "상대방 정보", required: true, previewEditableIds: ["dematic-mou-partner-name", "dematic-mou-partner-name-en", "dematic-mou-partner-short", "dematic-mou-partner-address", "dematic-mou-partner-address-en", "dematic-mou-partner-intro"] },
      { id: "contract-date", label: "MOU 각서 정보", required: false, previewEditableIds: ["dematic-mou-contract-date"] },
      // 서명일(supplierDate · partner.date)이 기본 상태에서 비어 기존 검토에 걸린다.
      { id: "signature", label: "서명 정보", required: true, previewEditableIds: ["dematic-mou-signature-left-title", "dematic-mou-signature-left-name", "dematic-mou-signature-left-date", "dematic-mou-signature-right-company", "dematic-mou-signature-right-companyEn", "dematic-mou-signature-right-title", "dematic-mou-signature-right-name", "dematic-mou-signature-right-date"] },
    ],
  },
  {
    key: "nda",
    kind: "document",
    workspace: true,
    label: "NDA",
    family: "contract-agreement",
    legacyConfigKey: "nda",
    routes: { crm: "/crm/document-automation/nda", automation: "/automation/nda" },
    menu: { label: "NDA", status: "active", path: "/crm/document-automation/nda", roleManifestLabel: "NDA" },
    execution: { path: "/crm/document-automation/nda", label: "NDA" },
    historyType: "nda",
    historyLabel: "NDA",
    impl: {
      pageFile: "NDAPage.tsx",
      previewComponent: "NDAPreview",
      printRootClass: "nda-print-root",
      docSurface: "nda",
      driveCaptureTarget: ".doc-preview-track",
      editable: true,
      printCssLocation: ["NDAPage.tsx", "DocStyles.css"],
      riskLevel: "high",
    },
    display: {
      tabLabel: "NDA",
      subheaderTitle: "NDA 작성기",
      badgeLabel: "NDA",
      driveFolderLabel: "NDA",
    },
    sections: [
      // 상대방 회사명·주소가 비면 "파트너사" · "주소 입력" placeholder 로 떨어져 기존 검토에 걸린다.
      { id: "partner", label: "상대방 정보", required: true, previewEditableIds: ["nda-party2-company", "nda-party2-address"] },
      { id: "doc-type", label: "문서 유형", required: false, previewEditableIds: ["nda-art1-purpose"] },
      // 서명일(colosseumDate · partner.date)이 기본 상태에서 비어 기존 검토에 걸린다.
      { id: "signature", label: "서명 정보", required: true, previewEditableIds: ["nda-signature-left-title", "nda-signature-left-name", "nda-signature-left-date", "nda-signature-right-company", "nda-signature-right-title", "nda-signature-right-name", "nda-signature-right-date"] },
    ],
  },
  {
    // ★ 해외 NDA (사용자 요청 2026-10-06) — 해외 파트너용 NDA 4종(단방향·양방향 × 국문·영문)을 한 화면에서 만든다.
    //   문구 정본은 nda/internationalNdaTemplate.ts(사용자 docx 4종). 머리글·바닥글·양식은 기존 NDA 와 같다.
    //   국내 NDA(nda)는 그대로 두고 별도 문서로 둔다 — 조항 수(13)·준거법/언어 조항이 달라 같은 이력에 섞지 않는다.
    //   사이드바 메뉴는 없다(문서는 /crm/documents 한 화면에서 고른다) — 계약서처럼 권한 목록에는 "(메뉴 숨김)" 으로 남는다.
    key: "nda-international",
    kind: "document",
    workspace: true,
    label: "해외 NDA",
    family: "contract-agreement",
    routes: { crm: "/crm/document-automation/nda-international" },
    execution: { path: "/crm/document-automation/nda-international", label: "해외 NDA" },
    historyType: "nda_international",
    historyLabel: "해외 NDA",
    impl: {
      pageFile: "nda/InternationalNDAPage.tsx",
      previewComponent: "InternationalNDAPreview",
      printRootClass: "nda-print-root",
      docSurface: "nda-international",
      driveCaptureTarget: ".doc-preview-track",
      editable: false,
      printCssLocation: ["nda/InternationalNDAPage.tsx", "DocStyles.css"],
      riskLevel: "high",
      notes: "조항 본문 편집 불가 — 변수만 폼에서 바꾼다(템플릿 버전 OVERSEAS_{ONEWAY|MUTUAL}_{KO|EN}_V1).",
    },
    display: {
      tabLabel: "해외 NDA",
      subheaderTitle: "해외 NDA 작성기",
      badgeLabel: "해외 NDA",
      driveFolderLabel: "NDA",
    },
    aliases: ["International NDA", "Overseas NDA", "해외 비밀유지계약"],
    sections: [
      // 파트너 법인명·주소가 비면 "[해외 파트너사 법인명]" · "[등록 주소 입력]" 자리표시로 남아 기존 검토에 걸린다.
      { id: "partner", label: "상대방 정보", required: true, previewEditableIds: ["ndai-party2-company", "ndai-party2-address", "ndai-intro-partner"] },
      { id: "doc-type", label: "문서 유형", required: false, previewEditableIds: ["ndai-intro-purpose", "ndai-art1-purpose"] },
      // V2: 계약기간 · 비밀유지 기간 · 분쟁 해결(법원/중재) · 준거법이 한 카드다.
      { id: "law", label: "계약 조건", required: false, previewEditableIds: ["ndai-term-years", "ndai-conf-years", "ndai-law", "ndai-court"] },
      // 서명일·파트너 서명자가 기본 상태에서 비어 기존 검토에 걸린다.
      { id: "signature", label: "서명 정보", required: true, previewEditableIds: ["ndai-signature-left-title", "ndai-signature-left-name", "ndai-signature-left-date", "ndai-signature-right-company", "ndai-signature-right-title", "ndai-signature-right-name", "ndai-signature-right-date"] },
    ],
  },
  {
    key: "order-approval",
    kind: "document",
    workspace: true,
    label: "수주 품의서",
    family: "internal-approval",
    legacyConfigKey: "orderApproval",
    routes: {
      crm: "/crm/document-automation/order-approval",
      automation: "/automation/order-approval",
    },
    menu: {
      label: "Sales Approval Form(수주품의서)",
      status: "beta",
      path: "/crm/document-automation/order-approval",
      roleManifestLabel: "수주품의서",
    },
    execution: { path: "/crm/document-automation/order-approval", label: "수주품의서" },
    historyType: "order_approval",
    historyLabel: "수주품의서",
    impl: {
      pageFile: "OrderApprovalPage.tsx",
      previewComponent: "OrderApprovalPreview",
      printRootClass: "order-approval-print-root",
      docSurface: "order-approval",
      driveCaptureTarget: ".doc-preview-track",
      editable: false,
      printCssLocation: ["OrderApprovalPage.tsx", "DocStyles.css"],
      riskLevel: "medium-high",
    },
    display: {
      tabLabel: "수주 품의서",
      subheaderTitle: "수주 품의서",
      badgeLabel: "수주 품의서",
      driveFolderLabel: "수주 품의서",
    },
    aliases: ["수주 품의서", "발주품의서"],
    sections: [
      { id: "counterparty", label: "상대방 정보", required: false },
      { id: "project", label: "프로젝트 기본정보", required: false },
      { id: "result", label: "계산 결과", required: false },
      { id: "external-quotes", label: "외부 견적", required: false },
      { id: "material", label: "재료비 입력", required: false },
      { id: "expense", label: "경비 입력", required: false },
      { id: "labor", label: "인건비 입력", required: false },
      { id: "margin", label: "이익률 / 지급 조건", required: false },
    ],
  },
  {
    key: "proposal",
    kind: "document",
    label: "제안서",
    routes: { crm: "/crm/document-automation/proposal" },
    menu: { label: "Proposal(제안서)", status: "beta", path: "/crm/document-automation/proposal", roleManifestLabel: "제안서(Proposal)" },
    historyType: "proposal",
    historyLabel: "제안서",
    excludeFromDocKeyHistory: true,
    impl: {
      pageFile: "ProposalPage.tsx",
      previewComponent: "ProposalWorkspace",
      printRootClass: "proposal-print-root",
      driveCaptureTarget: ".doc-preview-track",
      editable: true,
      printCssLocation: ["ProposalWorkspace.tsx", "DocStyles.css"],
      notes: "문서 자동화 페이지가 아니라 제안서 워크스페이스 — 리스크 미평가.",
    },
    // 제안서는 SectionCard 구조가 아니라 슬라이드/블록 구조라 섹션 정의를 미룬다.
    sections: [],
  },
] as const;

/**
 * 사이드바 묶음. 그룹은 메뉴에만 있는 개념이라(문서 속성이 아니라) 여기서 순서째로 잡는다.
 * 순서·라벨은 합치기 전 DOCUMENT_MENU_ITEMS 와 같다.
 */
export const DOCUMENT_MENU_LAYOUT: readonly (
  | DocKey
  | { groupKey: string; label: string; children: readonly DocKey[] }
)[] = [
  { groupKey: "quotation-group", label: "Quotation(견적서)", children: ["quote", "quote-other", "quote-equipment"] },
  "contracts",
  "invoice",
  { groupKey: "mou-group", label: "MOU", children: ["mou", "maintenance-mou", "mou-dematic"] },
  "nda",
  "order-approval",
  "proposal",
];

/**
 * 하위호환 PascalCase 실행 키 — 옛 파이프라인 데이터가 아직 이 키로 문서를 가리킨다.
 * 순서는 화면에 그대로 나오므로(단계 이동 모달) 합치기 전 배열 순서를 유지한다.
 */
export const LEGACY_EXECUTION_ALIASES: readonly { key: string; label: string; docKey: DocKey }[] = [
  { key: "Quotation", label: "견적서", docKey: "quote" },
  { key: "Contract", label: "계약서", docKey: "contract" },
  { key: "Invoice", label: "인보이스", docKey: "invoice" },
  { key: "Order Approval", label: "수주품의서", docKey: "order-approval" },
  { key: "MOU", label: "MOU", docKey: "mou" },
  { key: "Maintenance MOU", label: "유지보수 MOU", docKey: "maintenance-mou" },
  { key: "Dematic MOU", label: "Dematic MOU", docKey: "mou-dematic" },
  { key: "NDA", label: "NDA", docKey: "nda" },
];

/** docKey 가 없는 document_type 의 표시 라벨 — 서버에만 있고 작성 화면이 없는 종류. */
export const EXTRA_HISTORY_TYPE_LABELS: Readonly<Record<string, string>> = {
  equipment_spec_pack: "Equipment Spec Pack",
  // 3D 작업 이력(2026-10-07) — 문서가 아니라 도구 화면의 작업 저장. 단계 자료·문서에 이력으로 붙는다.
  rack_layout_3d: "창고 3D 랙 레이아웃",
  twin_layout: "디지털 트윈 레이아웃",
};

// ─── 조회 헬퍼 ──────────────────────────────────────────────────────────────

const BY_KEY = new Map<string, DocumentRegistryEntry>(
  DOCUMENT_REGISTRY.map((entry) => [entry.key, entry])
);

export function getDocumentEntry(key: string): DocumentRegistryEntry | undefined {
  return BY_KEY.get(key);
}

/** 문서만 (계약서 허브 같은 kind:"hub" 는 뺀다) */
export function documentEntries(): DocumentRegistryEntry[] {
  return DOCUMENT_REGISTRY.filter((entry) => entry.kind === "document");
}

/** 편집 섹션 목록 — 채울 항목 패널과 미리보기 블록이 같은 id 로 묶인다. */
export function documentSections(key: string): readonly DocumentSection[] {
  return getDocumentEntry(key)?.sections ?? [];
}

// ─── 경로·권한 ──────────────────────────────────────────────────────────────

/** 통합 작성 화면 경로 (목업 ①). 기존 /crm/document-automation/<key> 와 병행한다. */
export function documentWorkspacePath(key: string): string {
  return `/crm/documents/${key}`;
}

/**
 * 메뉴 권한 키. 통합 작성 화면(/crm/documents/<key>)도 **같은 키**를 쓴다 —
 * 경로가 둘이라고 권한 키를 둘로 만들면 한쪽만 조이는 구멍이 생긴다.
 */
export function documentMenuRoleKey(key: string): string {
  return `crm:document-automation:${key}`;
}

/**
 * 하위호환 별칭(PascalCase 등)까지 포함해 정본 docKey 로 되돌린다.
 * 옛 파이프라인 데이터가 "Quotation" 같은 키로 문서를 가리킨다.
 */
export function canonicalDocKey(key: string): DocKey | null {
  if (BY_KEY.has(key)) return key as DocKey;
  const alias = LEGACY_EXECUTION_ALIASES.find((item) => item.key === key);
  return alias ? alias.docKey : null;
}

/**
 * 임베드 캡처가 띄울 경로. 통합 작성 화면에 올라간 문서는 그 경로를 쓰고,
 * 아직 안 올라간 문서는 기존 실행 경로를 그대로 쓴다(둘 다 embedded=1 이면 문서만 남는다).
 */
export function documentCapturePath(key: string): string | null {
  const canonical = canonicalDocKey(key);
  if (!canonical) return null;
  const entry = BY_KEY.get(canonical);
  if (!entry) return null;
  if (entry.workspace) return documentWorkspacePath(canonical);
  return entry.execution?.path ?? entry.routes.crm;
}

/** 권한 관리 화면(/settings/menus)이 나열하는 문서 항목 — 사이드바 순서 그대로. */
export function documentRoleManifestEntries(): { key: string; label: string }[] {
  const fromMenu = DOCUMENT_MENU_LAYOUT.flatMap((slot) =>
    typeof slot === "string" ? [slot] : slot.children
  ).flatMap((docKey) => {
    const entry = BY_KEY.get(docKey);
    if (!entry?.menu) return [];
    return [{ key: documentMenuRoleKey(docKey), label: entry.menu.roleManifestLabel ?? entry.menu.label }];
  });

  // 사이드바 항목이 없는 문서(계약서 허브로 합쳐진 계약서·시스템 연동 계약서)도 경로는 살아 있고
  // 권한 게이트가 걸린다 → 관리 화면에서 고를 수 있게 목록에 남긴다.
  const hidden = DOCUMENT_REGISTRY.filter((entry) => entry.kind === "document" && !entry.menu).map((entry) => ({
    key: documentMenuRoleKey(entry.key),
    label: `${entry.label} (메뉴 숨김)`,
  }));

  return [...fromMenu, ...hidden];
}

/**
 * 메뉴 관리 화면에 보탤 **문서 권한 전용 행** — 키 · 표시 라벨 · 코드 경로.
 *
 * 사이드바에서는 문서 10줄이 "문서 템플릿" 한 줄로 합쳐졌지만, 문서별 권한 키는 그대로
 * 살아 있다(통합 화면과 구 경로를 그 키로 막는다). 그 권한을 관리 화면에서 손보려면
 * 행이 보여야 하므로 여기서 목록을 준다.
 *
 * ★ 예전엔 사이드바 생성물(menuCodeDefaults)이 이 행들을 같이 담았다. 그러다
 *   "사이드바에 없는 키가 생성물에 있다" ↔ "재생성하면 다시 들어온다" 가 세 번 반복됐다.
 *   생성물은 사이드바만, 이 행들은 여기(문서 정본)에서 온다 — 근거가 한 곳이어야 안 갈린다.
 *
 * 경로는 정본 경로(/crm/documents/<key>)다. 사이드바에도 있는 문서(제안서)는
 * 생성물에 이미 있으므로 호출측이 건너뛴다.
 */
export function documentPermissionRows(): { key: string; label: string; path: string }[] {
  return DOCUMENT_REGISTRY.filter((entry) => entry.kind === "document").map((entry) => ({
    key: documentMenuRoleKey(entry.key),
    label: entry.menu?.roleManifestLabel ?? entry.menu?.label ?? entry.label,
    path: entry.workspace ? documentWorkspacePath(entry.key) : entry.routes.crm,
  }));
}

/**
 * 경로 → docKey. 기존 경로와 통합 작성 화면을 같은 문서로 묶는다.
 * 겹치는 접두사가 있어(quote / quote-other) 가장 긴 경로가 이긴다.
 */
export function documentKeyForPath(pathname: string): DocKey | null {
  let bestKey: DocKey | null = null;
  let bestLen = -1;
  for (const entry of DOCUMENT_REGISTRY) {
    // ★ 메뉴에 없는 문서도 막는다. 계약서·시스템 연동 계약서는 사이드바 항목이 없어서
    //   (계약서 허브로 합쳐졌다) 메뉴 기준으로만 훑으면 경로가 그대로 뚫린다.
    const paths = [
      entry.menu ? entry.menu.absolutePath ?? entry.menu.path : null,
      entry.kind === "document" ? entry.routes.crm : null,
      entry.kind === "document" ? documentWorkspacePath(entry.key) : null,
    ];
    for (const path of paths) {
      if (!path) continue;
      if ((pathname === path || pathname.startsWith(`${path}/`) || pathname.startsWith(`${path}?`)) && path.length > bestLen) {
        bestKey = entry.key;
        bestLen = path.length;
      }
    }
  }
  return bestKey;
}

/**
 * 경로 → 문서 **권한 등급** 키(menu_role_map). 등급은 여전히 이 키가 갖는다.
 *
 * ★ 사용/미사용은 이 키가 더 이상 갖지 않는다. 메뉴를 끄면 문서가 통째로 막히던 것을
 *   떼어내 crm_document_catalog.enabled 로 옮겼다(사용자 지적: Dematic MOU).
 */
export function documentMenuKeyForPath(pathname: string): string | null {
  const key = documentKeyForPath(pathname);
  return key ? documentMenuRoleKey(key) : null;
}

/** 문서 전환 레일에 올리는 문서를 묶음 순서대로. 목업 ① 의 전환 칩·목록이 이걸 쓴다. */
export function documentFamilyGroups(): { family: DocumentFamily; label: string; entries: DocumentRegistryEntry[] }[] {
  return DOCUMENT_FAMILY_ORDER.map((family) => ({
    family,
    label: DOCUMENT_FAMILY_LABELS[family],
    entries: DOCUMENT_REGISTRY.filter((entry) => entry.family === family),
  })).filter((group) => group.entries.length > 0);
}

/** React Router 의 /crm 하위 상대 경로 ("document-automation/quote") */
export function crmRouteSegment(key: DocKey): string {
  const path = BY_KEY.get(key)?.routes.crm ?? "";
  return path.replace(/^\/crm\//, "");
}

/** React Router 의 /automation 하위 상대 경로 ("quote") */
export function automationRouteSegment(key: DocKey): string {
  const path = BY_KEY.get(key)?.routes.automation ?? "";
  return path.replace(/^\/automation\//, "");
}
